import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Calculator,
  ArrowRight,
  ArrowLeft,
  Check,
  Loader2,
  Building2,
  Briefcase,
  Home as HomeIcon,
  ShoppingBag,
  HeartPulse,
  TrendingDown,
} from "lucide-react";

const FACILITY_RATES = {
  warehouse: 5,
  office: 12,
  residential: 8,
  retail: 16,
  healthcare: 22,
};

const SERVICE_MULTS = {
  cleaning: 0.32,
  hvac: 0.35,
  fire: 0.08,
  manpower: 0.18,
  pest: 0.04,
};

// Savings % tier vs current vendor count
const SAVINGS_TIERS = {
  4: 22,
  3: 18,
  2: 12,
  1: 6,
};

// Edge-case guards
const CURRENT_COST_FLOOR = 6000;     // No micro-quotes; B2B buyers ignore tiny numbers
const RESULT_BAND_PCT = 0.15;        // Show ±15% range under headline

// Sqft slider bounds (UAE facility sizes)
const SQFT_MIN = 1000;
const SQFT_MAX = 500000;
const SQFT_STEP = 1000;
const SQFT_DEFAULT = 20000;
const TOTAL_STEPS = 5;

const F_ICONS = {
  warehouse: Building2,
  office: Briefcase,
  residential: HomeIcon,
  retail: ShoppingBag,
  healthcare: HeartPulse,
};

const formatAed = (n) =>
  new Intl.NumberFormat("en-AE", { style: "currency", currency: "AED", maximumFractionDigits: 0 }).format(n);

export default function CostCalculator() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    ftype: "warehouse",
    sqft: SQFT_DEFAULT,
    services: ["cleaning", "hvac"],
    vendors: 3,
    name: "",
    email: "",
    phone: "",
  });

  const totalSteps = TOTAL_STEPS;
  const ftypes = t("tools.calc.ftypes");
  const servicesList = t("tools.calc.servicesList");
  const vendorOptions = t("tools.calc.vendorOptions");

  const result = useMemo(() => {
    const base = form.sqft * (FACILITY_RATES[form.ftype] || 10);
    const services = form.services.reduce(
      (sum, s) => sum + base * (SERVICE_MULTS[s] || 0),
      0
    );
    // Floor: avoid micro-quotes that feel unreal to B2B buyers
    const currentCost = Math.max(services, CURRENT_COST_FLOOR);
    const savingsPct = SAVINGS_TIERS[Math.min(form.vendors, 4)] ?? 6;
    const orionCost = currentCost * (1 - savingsPct / 100);
    // ±15% band — quotes shown as a range, not a precise number
    const orionLow = Math.round(orionCost * (1 - RESULT_BAND_PCT));
    const orionHigh = Math.round(orionCost * (1 + RESULT_BAND_PCT));
    return {
      currentCost,
      orionCost,
      orionLow,
      orionHigh,
      annualSavings: currentCost - orionCost,
      savingsPct,
    };
  }, [form]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (id) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(id) ? f.services.filter((x) => x !== id) : [...f.services, id],
    }));
  };

  const canNext = () => {
    if (step === 1) return !!form.ftype;
    if (step === 2) return form.sqft > 0;
    if (step === 3) return form.services.length > 0;
    if (step === 4) return !!form.vendors;
    if (step === 5) return form.name && form.email && form.phone;
    return true;
  };

  const submit = async () => {
    if (!canNext()) return;
    setSubmitting(true);
    try {
      await apiClient.post("/cost-calculator", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        facility_type: form.ftype,
        sqft: form.sqft,
        services: form.services,
        vendor_count: form.vendors,
        estimated_current_cost: Math.round(result.currentCost),
        estimated_orion_cost: Math.round(result.orionCost),
        estimated_savings: Math.round(result.annualSavings),
        language: lang,
      });
      setDone(true);
      toast.success(t("common.successTitle"), { description: t("common.successMsg") });
    } catch {
      toast.error(t("common.errorMsg"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="cost-calculator-page">
      <SEO
        title={`UAE Maintenance Cost Calculator | ${COMPANY.brand}`}
        description="Free 60-second UAE facility cost calculator. Estimate annual cleaning, HVAC, AMC and manpower costs and see your savings with single-vendor consolidation."
      />

      <section className="bg-white py-16 lg:py-20">
        <div className="container-orion max-w-4xl">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-700">
            <ArrowLeft className="w-4 h-4 rtl-flip" />
            {t("nav.home")}
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
              <Calculator className="w-6 h-6" />
            </div>
            <div className="text-eyebrow">{t("tools.sectionLabel")}</div>
          </div>
          <h1 className="mt-5 font-heading text-3xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.05] text-slate-900">
            {t("tools.calc.title")}
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">{t("tools.calc.short")}</p>
        </div>
      </section>

      {/* Form card */}
      <section className="pb-24 bg-white">
        <div className="container-orion max-w-3xl">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-2xl shadow-blue-900/5 p-8 lg:p-12">
            {!done ? (
              <>
                {/* Progress */}
                <div className="flex items-center justify-between mb-10">
                  <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-700">
                    {t("tools.calc.step")} {step} {t("tools.calc.of")} {totalSteps}
                  </div>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <span
                        key={n}
                        className={`h-1.5 rounded-full transition-all ${
                          n <= step ? "bg-blue-600 w-8" : "bg-slate-200 w-4"
                        }`}
                      />
                    ))}
                  </div>
                </div>

                {/* Step 1: facility type */}
                {step === 1 && (
                  <div data-testid="calc-step-1">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900">
                      {t("tools.calc.s1Title")}
                    </h2>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {ftypes.map((f) => {
                        const Icon = F_ICONS[f.id] || Building2;
                        const active = form.ftype === f.id;
                        return (
                          <button
                            key={f.id}
                            type="button"
                            data-testid={`calc-ftype-${f.id}`}
                            onClick={() => update("ftype", f.id)}
                            className={`flex items-center gap-4 rounded-xl border p-5 text-left transition-all ${
                              active
                                ? "border-blue-600 bg-blue-50 ring-2 ring-blue-600/20"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <div
                              className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-700"
                              }`}
                            >
                              <Icon className="w-5 h-5" />
                            </div>
                            <span className="font-heading text-base font-medium text-slate-900">
                              {f.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step 2: sqft */}
                {step === 2 && (
                  <div data-testid="calc-step-2">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900">
                      {t("tools.calc.s2Title")}
                    </h2>
                    <p className="mt-2 text-slate-500 text-sm">{t("tools.calc.s2Hint")}</p>
                    <div className="mt-10">
                      <div className="flex items-baseline justify-between">
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                          {t("tools.calc.sqftLabel")}
                        </Label>
                        <span className="font-heading text-3xl font-medium text-blue-700">
                          {form.sqft.toLocaleString()}
                        </span>
                      </div>
                      <Slider
                        data-testid="calc-sqft-slider"
                        value={[form.sqft]}
                        min={SQFT_MIN}
                        max={SQFT_MAX}
                        step={SQFT_STEP}
                        onValueChange={(v) => update("sqft", v[0])}
                        className="mt-6"
                      />
                      <Input
                        type="number"
                        value={form.sqft}
                        onChange={(e) => update("sqft", parseInt(e.target.value || "0", 10))}
                        className="mt-6 h-12 rounded-lg"
                        data-testid="calc-sqft-input"
                      />
                    </div>
                  </div>
                )}

                {/* Step 3: services */}
                {step === 3 && (
                  <div data-testid="calc-step-3">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900">
                      {t("tools.calc.s3Title")}
                    </h2>
                    <p className="mt-2 text-slate-500 text-sm">{t("tools.calc.s3Hint")}</p>
                    <div className="mt-8 space-y-3">
                      {servicesList.map((s) => {
                        const checked = form.services.includes(s.id);
                        return (
                          <label
                            key={s.id}
                            data-testid={`calc-service-${s.id}`}
                            className={`w-full flex items-center gap-4 rounded-xl border p-5 text-left cursor-pointer transition-all ${
                              checked
                                ? "border-blue-600 bg-blue-50"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleService(s.id)}
                            />
                            <span className="font-heading text-base font-medium text-slate-900">
                              {s.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step 4: vendors */}
                {step === 4 && (
                  <div data-testid="calc-step-4">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900">
                      {t("tools.calc.s4Title")}
                    </h2>
                    <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {vendorOptions.map((v) => {
                        const active = form.vendors === v.value;
                        return (
                          <button
                            key={v.value}
                            type="button"
                            data-testid={`calc-vendors-${v.value}`}
                            onClick={() => update("vendors", v.value)}
                            className={`rounded-xl border px-5 py-6 text-center transition-all ${
                              active
                                ? "border-blue-600 bg-blue-50 ring-2 ring-blue-600/20"
                                : "border-slate-200 bg-white hover:border-slate-300"
                            }`}
                          >
                            <span className="font-heading text-sm font-medium text-slate-900">
                              {v.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Step 5: contact */}
                {step === 5 && (
                  <div data-testid="calc-step-5">
                    <h2 className="font-heading text-2xl sm:text-3xl tracking-tight font-medium text-slate-900">
                      {t("tools.calc.s5Title")}
                    </h2>
                    <div className="mt-8 space-y-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                          {t("contact.formName")}
                        </Label>
                        <Input
                          data-testid="calc-name"
                          value={form.name}
                          onChange={(e) => update("name", e.target.value)}
                          className="h-12 rounded-lg"
                          required
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                            {t("contact.formEmail")}
                          </Label>
                          <Input
                            data-testid="calc-email"
                            type="email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            className="h-12 rounded-lg"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label className="text-xs uppercase tracking-[0.18em] text-slate-500">
                            {t("contact.formPhone")}
                          </Label>
                          <Input
                            data-testid="calc-phone"
                            value={form.phone}
                            onChange={(e) => update("phone", e.target.value)}
                            placeholder="+971 ..."
                            className="h-12 rounded-lg"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Nav */}
                <div className="mt-10 flex items-center justify-between">
                  <button
                    type="button"
                    data-testid="calc-back"
                    onClick={() => setStep((s) => Math.max(1, s - 1))}
                    disabled={step === 1}
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ArrowLeft className="w-4 h-4 rtl-flip" />
                    {t("tools.calc.back")}
                  </button>
                  {step < totalSteps ? (
                    <button
                      type="button"
                      data-testid="calc-next"
                      onClick={() => setStep((s) => s + 1)}
                      disabled={!canNext()}
                      className="btn-primary-orion disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {t("tools.calc.next")}
                      <ArrowRight className="w-4 h-4 rtl-flip" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="calc-submit"
                      onClick={submit}
                      disabled={submitting || !canNext()}
                      className="btn-primary-orion disabled:opacity-50"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t("common.submitting")}
                        </>
                      ) : (
                        <>
                          {t("tools.calc.finish")}
                          <ArrowRight className="w-4 h-4 rtl-flip" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </>
            ) : (
              /* RESULT */
              <div data-testid="calc-result" className="fade-up">
                <div className="text-eyebrow text-blue-700 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  {t("tools.calc.resultEyebrow")}
                </div>
                <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
                  {t("tools.calc.resultTitle")}
                </h2>

                <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-2xl overflow-hidden">
                  <div className="bg-white p-6">
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
                      {t("tools.calc.currentLabel")}
                    </div>
                    <div className="mt-3 font-heading text-2xl tracking-tight font-medium text-slate-400 line-through">
                      {formatAed(result.currentCost)}
                    </div>
                  </div>
                  <div className="bg-white p-6">
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-700">
                      {t("tools.calc.orionLabel")}
                    </div>
                    <div className="mt-3 font-heading text-2xl tracking-tight font-medium text-slate-900">
                      {formatAed(result.orionCost)}
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      {t("tools.calc.bandLabel")} {formatAed(result.orionLow)} – {formatAed(result.orionHigh)}
                    </div>
                  </div>
                  <div className="bg-blue-600 text-white p-6">
                    <div className="text-xs uppercase tracking-[0.18em] font-semibold text-blue-100 flex items-center gap-1.5">
                      <TrendingDown className="w-3.5 h-3.5" />
                      {t("tools.calc.savingsLabel")}
                    </div>
                    <div className="mt-3 font-heading text-2xl tracking-tight font-medium">
                      {formatAed(result.annualSavings)}
                    </div>
                    <div className="mt-1 text-xs text-blue-100">
                      {result.savingsPct}% {t("tools.calc.savingsPctLabel")}
                    </div>
                  </div>
                </div>

                <p className="mt-8 text-sm text-slate-500 leading-relaxed">{t("tools.calc.disclaimer")}</p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <button
                    type="button"
                    data-testid="calc-book"
                    onClick={() => navigate("/contact")}
                    className="btn-primary-orion"
                  >
                    {t("tools.calc.bookCta")}
                    <ArrowRight className="w-4 h-4 rtl-flip" />
                  </button>
                  <a
                    href={`https://wa.me/${COMPANY.whatsapp}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary-orion"
                  >
                    {t("common.whatsapp")}
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
